import React from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { SCOPE_TAG, formatBlackoutDates } from "helpers/blackouts"
import styles from "./index.module.css"

/**
 * What the delete confirmation says about the blackout being deleted.
 *
 * "Are you sure?" on its own is a dangerous question to answer from three different surfaces — the
 * settings list, the calendar day panel and the availability drawer all open the same dialog, and
 * from a day panel there is no row on screen to check against. So the dialog names the record: its
 * scope and its dates.
 *
 * The scope tag's solid label is reused rather than the record's own `label`, because that is the
 * wording the rest of the feature already uses for a record standing next to a date ("Crew
 * Blackout"), while `label` is whatever the feed happened to send ("Crew OFF").
 */
const DeleteBlackoutMessage = ({ blackout, date }) => {
  const { t } = useTranslation()

  const tag = SCOPE_TAG[blackout?.scope]
  const title = tag ? t(tag.solidLabelKey, { defaultValue: tag.solidLabelDefault }) : blackout?.label || ""
  const dates = formatBlackoutDates(blackout, date)

  return (
    <span className={styles.message}>
      <span className={styles.lead}>
        {t("settings.blackout.delete_message", { defaultValue: "You are about to delete a blackout:" })}
      </span>
      <span className={styles.record}>
        {title && <span className={styles.recordLine}>{title}</span>}
        {dates && <span className={styles.recordLine}>{dates}</span>}
      </span>
    </span>
  )
}

DeleteBlackoutMessage.propTypes = {
  blackout: PropTypes.shape({
    scope: PropTypes.string,
    label: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    entries: PropTypes.array
  }),
  /** The day the dialog was opened from, used when the record carries no dates of its own. */
  date: PropTypes.string
}

export default DeleteBlackoutMessage
